import { type FC, type PropsWithChildren, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { Glow } from '@/components/Shop/ui/ShopUi';
import { Picture } from '@/components/ui/Picture/Picture';

import css from './styles.module.scss';

import lightPng from '/img/shop/v2/card-light.png';
import lightWebp from '/img/shop/v2/card-light.webp';
import vipBadge from '/img/shop/v2/vip-badge.svg';

interface TgemCardProps extends PropsWithChildren {
  /** Big number on top */
  amount: ReactNode;
  /** Second line: a caption ("Daily Bonus") or the free bonus */
  caption?: string;
  bonus?: number;
  vipDays?: number;
  /** Pack art, 136px box at the bottom */
  art: ReactNode;
  /** Corner badge (POPULAR / BEST DEAL) */
  badge?: ReactNode;
  marker?: boolean;
  /** Nothing to take right now: dimmed, no light */
  inactive?: boolean;
  className?: string;
}

/** 190×224 tile of the T-Gem and Dice sections. */
const TgemCard: FC<TgemCardProps> = ({
  amount,
  caption,
  bonus,
  vipDays,
  art,
  badge,
  marker,
  inactive,
  className,
  children,
}) => {
  const { t } = useTranslation();

  return (
    <div className={cn(css.card, className)}>
      <div className={css.bg}>
        <Glow
          color="#9955FF"
          blur={32}
          className="w-[104px] h-16 left-1/2 -translate-x-1/2 -top-2"
        />
        {!inactive && (
          <Glow
            color="#9955FF"
            className="size-40 left-1/2 -translate-x-1/2 -top-[131px]"
          />
        )}
        <Glow
          color="#9955FF"
          blur={32}
          className="w-full h-[104px] left-1/2 -translate-x-1/2 top-[calc(50%_+_40px)] -translate-y-1/2"
        />
        {!inactive && (
          <Picture
            img={lightPng}
            webp={lightWebp}
            alt=""
            className={css.light}
          />
        )}
      </div>

      <div className={cn(css.art, inactive && 'opacity-50')}>{art}</div>

      <div className={cn(css.info, inactive && 'opacity-50')}>
        <span className={css.amount}>{amount}</span>
        {caption && <span className={css.caption}>{t(caption)}</span>}
        {!!bonus && (
          <div className="flex items-end justify-center gap-1">
            <span className={css.bonus}>+{bonus}</span>
            <span className={css.freePill}>{t('Free')}</span>
          </div>
        )}
        {!!vipDays && (
          <div className="flex items-center justify-center gap-1">
            <span className={css.vipDays}>
              +{vipDays} {t(vipDays === 1 ? 'Day' : 'Days')}
            </span>
            <img src={vipBadge} alt="" className="size-4" />
          </div>
        )}
      </div>

      <div className={css.button}>{children}</div>
      {badge}
      {marker && <div className={css.marker} />}
    </div>
  );
};

export { TgemCard };
